import { useEffect, useState } from 'react';
import { Content, PageWithHeader, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import { Navigate } from 'react-router-dom';
import { getDocumentationEntities } from './docsUtils';

const DOCUMENTS = {
  engineering: {
    title: 'Spin Engineering',
    names: ['spin-engineering-docs'],
  },
  cli: {
    title: 'Spin Engineering CLI',
    names: ['spin-engineering-cli'],
  },
};

export const DocsRedirect = ({
  document,
}: {
  document: keyof typeof DOCUMENTS;
}) => {
  const catalogApi = useApi(catalogApiRef);
  const { title, names } = DOCUMENTS[document];
  const [result, setResult] = useState<{
    document: string;
    path?: string;
    error?: string;
  }>();

  useEffect(() => {
    let active = true;

    const resolveDocument = async () => {
      try {
        const response = await catalogApi.getEntities({
          filter: { 'spec.type': 'documentation' },
        });
        const entity = getDocumentationEntities(response).find(candidate => {
          if (
            candidate.kind.toLowerCase() !== 'component' ||
            candidate.spec?.type !== 'documentation'
          ) {
            return false;
          }
          if (document === 'engineering') {
            return (
              names.includes(candidate.metadata.name) &&
              (candidate.metadata.namespace || 'default') === 'default'
            );
          }
          return (
            candidate.metadata.title?.trim().toLowerCase() ===
              title.toLowerCase() || names.includes(candidate.metadata.name)
          );
        });

        if (!active) return;

        setResult(
          entity
            ? {
                document,
                path: `/docs/${
                  entity.metadata.namespace || 'default'
                }/${entity.kind.toLowerCase()}/${entity.metadata.name}`,
              }
            : {
                document,
                error: `No se encontró la documentación de ${title} en el catálogo.`,
              },
        );
      } catch {
        if (active) {
          setResult({
            document,
            error: `No fue posible cargar la documentación de ${title}.`,
          });
        }
      }
    };

    resolveDocument();
    return () => {
      active = false;
    };
  }, [catalogApi, document, names, title]);

  const currentResult = result?.document === document ? result : undefined;

  if (currentResult?.path) {
    return <Navigate to={currentResult.path} replace />;
  }

  return (
    <PageWithHeader title={title} themeId="documentation">
      <Content>
        {currentResult?.error ? (
          <Typography color="error" role="alert">
            {currentResult.error}
          </Typography>
        ) : (
          <Progress />
        )}
      </Content>
    </PageWithHeader>
  );
};
